<template>
  <div class="login-overlay" @click.self="onCancel">
    <div class="login-card">

      <div class="login-header">
        <h2>{{ t('emailCodeTitle') }}</h2>
        <button class="close-btn" @click="onCancel">&#10005;</button>
      </div>

      <div class="form">
        <p class="info-message">{{ t('emailCodeBody') }}</p>

        <input
            ref="codeInput"
            v-model="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            class="code-input"
            :placeholder="t('enterCode')"
            @input="onCodeInput"
            @keyup.enter="confirm"
        />

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <div v-if="infoMessage" class="success-message">{{ infoMessage }}</div>

        <button :disabled="!canConfirm || busy" @click="confirm">
          {{ t('confirm') }}
        </button>

        <button class="secondary-btn" :disabled="busy || resendCooldown > 0" @click="resend">
          {{ resendCooldown > 0 ? t('resendTooSoon') : t('resendCode') }}
        </button>

        <button class="link-btn" @click="onCancel">{{ t('cancel') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from '../i18n.js'

const RESEND_COOLDOWN_SECONDS = 30

export default {
  name: 'EmailCodeDialog',

  props: {
    service: { type: Object, required: true },
    email: { type: String, required: true }
  },

  emits: ['success', 'cancel'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      code: '',
      busy: false,
      errorMessage: '',
      infoMessage: '',
      resendCooldown: RESEND_COOLDOWN_SECONDS,
      cooldownTimer: null
    }
  },

  computed: {
    canConfirm() {
      return /^\d{6}$/.test(this.code)
    }
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown)
    this.startCooldown()
    this.$nextTick(() => this.$refs.codeInput?.focus())
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    if (this.cooldownTimer) clearInterval(this.cooldownTimer)
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.onCancel()
    },

    onCodeInput() {
      // Keep digits only; the backend code is strictly 6 numeric chars.
      this.code = this.code.replace(/\D/g, '').slice(0, 6)
      this.errorMessage = ''
    },

    startCooldown() {
      this.resendCooldown = RESEND_COOLDOWN_SECONDS
      if (this.cooldownTimer) clearInterval(this.cooldownTimer)
      this.cooldownTimer = setInterval(() => {
        if (this.resendCooldown > 0) this.resendCooldown--
        else if (this.cooldownTimer) clearInterval(this.cooldownTimer)
      }, 1000)
    },

    async confirm() {
      if (!this.canConfirm || this.busy) return
      this.busy = true
      this.errorMessage = ''
      this.infoMessage = ''
      try {
        const res = await this.service.loginWithEmailCode(this.email, this.code)
        if (res && res.loggedIn) {
          this.$emit('success', res)
        } else {
          this.errorMessage = this.t('codeInvalid')
        }
      } catch (e) {
        this.errorMessage = this.t('codeInvalid')
      } finally {
        this.busy = false
      }
    },

    async resend() {
      if (this.busy || this.resendCooldown > 0) return
      this.errorMessage = ''
      try {
        await this.service.requestEmailCode(this.email)
      } catch (e) { /* neutral */ }
      this.infoMessage = this.t('emailCodeBody')
      this.startCooldown()
    },

    onCancel() {
      if (this.busy) return
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 120;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 18px; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.login-card {
  width: min(420px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-message {
  margin: 0;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.code-input {
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 24px;
  letter-spacing: 8px;
  text-align: center;
}

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}
button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}
.secondary-btn:disabled { background: #e9eaef; color: #9aa0ac; }
.secondary-btn:hover:not(:disabled) { background: #e4e8ee; }

.link-btn {
  background: none;
  color: #6b7280;
  font-size: 13px;
  padding: 4px;
}
.link-btn:hover { color: #2563eb; }

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
